import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Clock, DollarSign, Printer } from 'lucide-react';
import { toast } from 'sonner';
import apiClient, { getAuthToken } from '@/utils/api';
import { jwtDecode } from 'jwt-decode';

const OrdersPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('all');
  const [userPermissions, setUserPermissions] = useState([]);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      navigate('/login');
      return;
    }
    try {
      const decoded = jwtDecode(token);
      let perms = decoded.permissions || ['orders'];
      if (decoded.user_type === 'owner') {
        setUserPermissions(['all']);
      } else {
        const expandedPerms = new Set(perms);
        if (perms.includes('orders')) {
          expandedPerms.add('view_orders').add('checkout_orders').add('print_receipt').add('view_past_orders').add('print_past_receipt');
        }
        setUserPermissions(Array.from(expandedPerms));
      }
    } catch (e) {
      console.error(e);
    }
    
    fetchOrders();
    const interval = setInterval(() => {
      fetchOrders();
    }, 5000);
    return () => clearInterval(interval);
  }, [navigate]);

  const hasPermission = (p) => userPermissions.includes('all') || userPermissions.includes(p);

  const fetchOrders = async () => {
    try {
      const response = await apiClient.get('/orders');
      setOrders(response.data);
    } catch (error) {
      console.error('Siparişler yüklenirken hata:', error);
    }
  };

  const handleStatusChange = async (group, newStatus) => {
    try {
      await Promise.all(group.order_ids.map(id => 
        apiClient.put(`/orders/${id}/status`, { status: newStatus })
      ));
      toast.success('Sipariş durumu güncellendi');
      fetchOrders();
    } catch (error) {
      toast.error('Durum güncellenirken hata oluştu');
    }
  };

  const printCombinedReceipt = async (group) => {
    try {
      const businessName = localStorage.getItem('business_name') || 'Cularix POS';
      const date = new Date().toLocaleString('tr-TR');
      
      let receiptText = `
========================================
${businessName.padStart(20 + Math.floor(businessName.length/2), ' ')}
========================================

Masa: ${group.table_number}
Tarih: ${date}

----------------------------------------
Ürün                      Adet    Tutar
----------------------------------------
`;

      group.items.forEach(item => {
        const name = item.menu_item_name.substring(0, 24).padEnd(25, ' ');
        const qty = item.quantity.toString().padStart(5, ' ');
        const total = (item.price * item.quantity).toFixed(2).padStart(8, ' ');
        receiptText += `${name} ${qty} ${total}₺\n`;
      });

      receiptText += `
----------------------------------------
TOPLAM:                          ${group.total_amount.toFixed(2).padStart(8, ' ')}₺
========================================

Teşekkür ederiz!
`;

      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
      iframe.contentWindow.document.write(`<pre style="font-family: monospace; font-size: 12px;">${receiptText}</pre>`);
      iframe.contentWindow.document.close();
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
      
      toast.success('Adisyon yazdırılıyor');
      
    } catch (error) {
      toast.error('Adisyon yazdırılamadı');
    }
  };

  // SADECE aktif (Tamamlanmamış) siparişleri göster ve GRUPLA
  const activeOrders = orders.filter(order => order.status !== 'completed');

  const groupedOrders = [];
  activeOrders.forEach(order => {
    const existingGroup = groupedOrders.find(g => g.table_number === order.table_number && g.status === order.status);
    
    if (existingGroup) {
      existingGroup.order_ids.push(order.id);
      existingGroup.total_amount += order.total_amount;
      
      order.items.forEach(newItem => {
        const existingItem = existingGroup.items.find(i => i.menu_item_name === newItem.menu_item_name && i.price === newItem.price);
        if (existingItem) {
          existingItem.quantity += newItem.quantity;
        } else {
          existingGroup.items.push({ ...newItem });
        }
      });
      
      if (new Date(order.created_at) < new Date(existingGroup.created_at)) {
        existingGroup.created_at = order.created_at;
      }
    } else {
      groupedOrders.push({
        id: `group-${order.table_number}-${order.status}-${order.id}`, // Benzersiz ID
        order_ids: [order.id],
        table_number: order.table_number,
        status: order.status,
        total_amount: order.total_amount,
        items: JSON.parse(JSON.stringify(order.items)),
        created_at: order.created_at
      });
    }
  });

  const filteredOrders = groupedOrders.filter(group => {
    if (filter === 'all') return true;
    return group.status === filter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'preparing':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'ready':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'completed':
        return 'bg-slate-500/20 text-slate-600 dark:text-slate-400 border-slate-500/30';
      default:
        return 'bg-slate-500/20 text-slate-600 dark:text-slate-400 border-slate-500/30';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Bekliyor';
      case 'preparing':
        return 'Hazırlanıyor';
      case 'ready':
        return 'Hazır';
      case 'completed':
        return 'Tamamlandı';
      default:
        return status;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Sipariş Takibi
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1">Mutfak siparişlerini ve adisyon durumlarını yönetin</p>
          </div>
          <div className="flex items-center gap-4">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="order-filter" className="w-48 bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                <SelectItem value="all" className="text-slate-800 dark:text-slate-200">Tüm Siparişler</SelectItem>
                <SelectItem value="pending" className="text-slate-800 dark:text-slate-200">Bekliyor</SelectItem>
                <SelectItem value="preparing" className="text-slate-800 dark:text-slate-200">Hazırlanıyor</SelectItem>
                <SelectItem value="ready" className="text-slate-800 dark:text-slate-200">Hazır</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredOrders.map((group) => (
            <Card
              key={group.id}
              data-testid={`order-group-card-${group.table_number}`}
              className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 p-6 hover:border-orange-500/30 transition-colors"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Masa {group.table_number}</h3>
                  <div className="flex items-center text-slate-600 dark:text-slate-400 text-sm mt-1">
                    <Clock size={14} className="mr-1" />
                    {new Date(group.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
                <Badge className={`${getStatusColor(group.status)} border`}>
                  {getStatusText(group.status)}
                  {group.order_ids.length > 1 && ` (${group.order_ids.length} Birleşik)`}
                </Badge>
              </div>

              <div className="space-y-2 mb-4">
                {group.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-slate-700 dark:text-slate-300">
                      {item.quantity}x {item.menu_item_name}
                    </span>
                    <span className="text-slate-600 dark:text-slate-400 font-mono">{(item.price * item.quantity).toFixed(2)} ₺</span>
                  </div>
                ))}
                {group.items.some(i => i.notes) && (
                  <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 rounded-lg">
                    <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1">📝 Sipariş Notu</p>
                    {group.items.filter(i => i.notes).map((i, idx) => (
                      <p key={idx} className="text-xs text-amber-800 dark:text-amber-300">{i.notes}</p>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-slate-200 dark:border-slate-800 mb-4">
                <span className="text-slate-600 dark:text-slate-400 font-medium">Toplam</span>
                <span className="text-orange-400 font-mono font-bold text-xl">{group.total_amount.toFixed(2)} ₺</span>
              </div>

              <div className="space-y-2">
                <Select
                  value={group.status}
                  onValueChange={(value) => handleStatusChange(group, value)}
                >
                  <SelectTrigger data-testid={`order-status-group-${group.table_number}`} className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                    <SelectItem value="pending" className="text-slate-800 dark:text-slate-200">Bekliyor</SelectItem>
                    <SelectItem value="preparing" className="text-slate-800 dark:text-slate-200">Hazırlanıyor</SelectItem>
                    <SelectItem value="ready" className="text-slate-800 dark:text-slate-200">Hazır</SelectItem>
                  </SelectContent>
                </Select>
                
                <div className="flex gap-2">
                  {hasPermission('print_receipt') && (
                    <Button
                      onClick={() => printCombinedReceipt(group)}
                      className="flex-1 bg-slate-200 dark:bg-slate-700 hover:bg-slate-600 text-slate-900 dark:text-white font-bold"
                    >
                      <Printer size={18} className="mr-2" />
                      Adisyon Yazdır
                    </Button>
                  )}
                  {hasPermission('checkout_orders') && (
                    <Button
                      data-testid={`checkout-group-${group.table_number}`}
                      onClick={() => navigate(`/admin/checkout/${group.table_number}`)}
                      className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-bold"
                    >
                      Hesaba Git
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>

        {filteredOrders.length === 0 && (
          <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 p-12 text-center">
            <p className="text-slate-600 dark:text-slate-400">Sipariş bulunamadı</p>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default OrdersPage;